import React from 'react';
import classNames from 'classnames';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import FlairIcon from '../../../../../atomic/atoms/FlairIcon';
import FormattedCurrency from '../../../../../components/currency/FormattedCurrency';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { bandPenetration, isOutOfBand } from '../logic';
import { PayBand } from '../types';
import './PayBandCell.css';

type Props = {
  payBand: PayBand | null;
  amount: number;
  // The row's salary currency — the band's min/max are expressed in it.
  currency: string;
  // Optional tooltip content. Defaults to the band's min–max range.
  tooltip?: React.ReactNode;
};

// Informational pay-band indicator: a min–max bar with a marker at the proposed
// amount. Out-of-band proposals are highlighted but never block submission.
//
// On multi-currency orgs the band may have been converted from the position's
// own currency and/or restated from its own pay frequency into the row's. When
// that happens the labels are prefixed with `~` (approximate) and a warning
// icon explains what was converted.
export const PayBandCell: React.FC<Props> = ({
  payBand,
  amount,
  currency,
  tooltip,
}) => {
  const t = useNamespacedTranslation('compensationReviews.table');

  if (!payBand) {
    return (
      <div className="compensation-pay-band">
        <div
          className="compensation-pay-band__empty"
          title={t('noPayBand')}
          aria-label={t('noPayBand')}
        />
      </div>
    );
  }

  const outOfBand = isOutOfBand(amount, payBand);
  const markerLeft = `${bandPenetration(amount, payBand)}%`;
  const approximate = payBand.currencyConverted || payBand.frequencyConverted;
  // `~` prefix on the approximated labels.
  const prefix = approximate ? '~' : '';

  const warnings: string[] = [];
  if (payBand.currencyConverted && payBand.definedCurrency) {
    warnings.push(
      t('payBandConvertedCurrency', { currency: payBand.definedCurrency }),
    );
  }
  if (payBand.frequencyConverted && payBand.definedFrequency) {
    warnings.push(
      t('payBandConvertedFrequency', {
        frequency: t(`frequencies.${payBand.definedFrequency.toLowerCase()}`),
      }),
    );
  }

  const boundary = (value: number) => (
    <>
      {prefix}
      <FormattedCurrency value={value} currency={currency} />
    </>
  );

  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip id={`pay-band-${payBand.min}-${payBand.max}`}>
          {tooltip ?? (
            <>
              {boundary(payBand.min)}
              {' – '}
              {boundary(payBand.max)}
              {outOfBand ? ` · ${t('outOfBand')}` : ''}
              {warnings.map((message) => (
                <span className="d-block mt-1" key={message}>
                  {message}
                </span>
              ))}
            </>
          )}
        </Tooltip>
      }>
      <div className="compensation-pay-band">
        <div
          className={classNames('compensation-pay-band__track', {
            'compensation-pay-band__track--out-of-band': outOfBand,
          })}>
          <div
            className={classNames('compensation-pay-band__marker', {
              'compensation-pay-band__marker--out-of-band': outOfBand,
            })}
            style={{ left: markerLeft }}
          />
        </div>
        <div className="d-flex justify-content-between align-items-center text-muted small mt-1">
          <span>{boundary(payBand.min)}</span>
          {approximate && (
            <FlairIcon
              icon="alert-triangle-outline"
              size="sm"
              className="compensation-pay-band__warning"
              aria-label={warnings.join(' ')}
            />
          )}
          <span>{boundary(payBand.max)}</span>
        </div>
      </div>
    </OverlayTrigger>
  );
};
